import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Heart, ArrowRight } from 'lucide-react';
import { getCompatibilitySession } from './services/compatibleApi';
import type { CompatibilitySession } from './types/compatible';

const CompatibleResult: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [session, setSession] = useState<CompatibilitySession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSession = async () => {
      if (!sessionId) return;

      try {
        const sessionData = await getCompatibilitySession(sessionId);
        setSession(sessionData);
      } catch (err) {
        setError('Session non trouvée');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSession();
  }, [sessionId]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-pulse text-slate-600">Chargement des résultats...</div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="text-center text-error p-4">
        {error || 'Session non trouvée'}
      </div>
    );
  }

  const { score = 0 } = session;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 animate-fade-in">
      <div className="text-center mb-8">
        <Heart className="mx-auto text-red-500 mb-4" size={48} />
        <h1 className="text-3xl font-bold mb-2">Résultats de Compatibilité</h1>
      </div>

      <div className="card p-8 text-center mb-8">
        <div className="mb-6">
          <div className="text-6xl font-bold text-sky-500 mb-2">
            {score}%
          </div>
          <p className="text-xl text-slate-600">
            de compatibilité
          </p>
        </div>

        <div className="space-y-4 text-left">
          <h2 className="text-xl font-semibold">Analyse</h2>
          <p className="text-slate-600">
            Votre score indique une {score >= 75 ? 'excellente' : score >= 50 ? 'bonne' : 'moyenne'} compatibilité.
            {score >= 75 
              ? ' Vous partagez une connexion exceptionnelle !'
              : score >= 50
              ? ' Il y a un bon potentiel entre vous.'
              : ' Il y a des aspects à améliorer dans votre relation.'}
          </p>

          <h2 className="text-xl font-semibold pt-4">Recommandations</h2>
          <ul className="list-disc list-inside space-y-2 text-slate-600">
            <li>Continuez à communiquer ouvertement</li>
            <li>Prenez le temps d'écouter votre partenaire</li>
            <li>Célébrez vos différences</li>
          </ul>
        </div>
      </div>

      <div className="text-center">
        <Link 
          to="/compatible"
          className="btn-primary inline-flex items-center gap-2"
        >
          Faire un nouveau test
          <ArrowRight size={18} />
        </Link>
      </div>
    </div>
  );
};

export default CompatibleResult;